import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useState } from 'react';

export default function FAQs() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqCategories = [
    {
      category: 'Getting Started',
      icon: 'ri-rocket-line',
      questions: [
        {
          question: 'How does Stashly work?',
          answer: 'Stashly connects people who need storage space with those who have extra space to rent. Renters can browse available spaces, book directly through our platform, and pay securely online. Hosts list their spaces, set their own prices, and earn money from unused areas like garages, basements, or sheds.',
        },
        {
          question: 'Is Stashly available in my area?',
          answer: 'Stashly operates across Australia, including major cities such as Sydney, Melbourne, Brisbane, Perth, Adelaide and Hobart, as well as rural and regional areas nationwide. The only limitation is the type and amount of space each host chooses to list. Whether it is a garage, shed, driveway, spare room, land or car space, availability is defined by what you have to offer. As more hosts join the platform, Stashly continues to grow its reach across the country.',
        },
        {
          question: 'How do I create an account?',
          answer: 'Click the "Sign Up" button in the top navigation. You\'ll need to provide your name, email address, and create a password. Identity verification is required before you can list spaces, and may be required for bookings. Verification is completed via our secure identity verification process.',
        },
        {
          question: 'Is there a fee to join Stashly?',
          answer: 'Creating an account on Stashly is completely free. We only charge service fees when you complete a booking or receive payment as a host. Renters pay a 10% service fee, and hosts pay a 10% service fee on each transaction.',
        },
      ],
    },
    {
      category: 'For Renters',
      icon: 'ri-user-line',
      questions: [
        {
          question: 'How do I book a storage space?',
          answer: 'Browse available spaces using our search filters, select a space that meets your needs, choose your rental period, and click "Book Now". You\'ll need to provide payment information and agree to the host\'s terms. Once confirmed, you\'ll receive access details and can start using the space on your selected start date.',
        },
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept all major credit cards (Visa, Mastercard), and debit cards. All payments are processed securely through our encrypted payment system. Your payment information is never shared with hosts.',
        },
        {
          question: 'What if I need to cancel my booking?',
          answer: 'Cancellation policies are set at the time of booking and displayed before you confirm. Refunds may be available depending on the timing of your cancellation and the applicable policy. Where required by the Australian Consumer Law, Stashly will provide appropriate remedies. Contact support@stashly.com.au to request a cancellation or refund.',
        },
        {
          question: 'Can I access my items anytime?',
          answer: 'Access schedules are set by individual hosts and clearly stated in each listing. Some hosts offer 24/7 access, whilst others have specific hours. Make sure to review and agree to the access terms before booking. If you need different access arrangements, message the host before booking.',
        },
        {
          question: 'What items am I not allowed to store?',
          answer: 'Prohibited items include: hazardous or flammable materials, illegal substances, weapons or ammunition, perishable food, live animals or plants, stolen property, items with strong odours, and toxic or radioactive materials. Storing prohibited items may result in immediate termination of your rental and account suspension.',
        },
      ],
    },
    {
      category: 'For Hosts',
      icon: 'ri-home-smile-line',
      questions: [
        {
          question: 'How do I list my space on Stashly?',
          answer: 'Click "List Your Space" in the navigation, create a host account if you haven\'t already, then provide details about your space including location, size, type, amenities, and pricing. Upload clear photos, set your availability and access rules, and submit for review. Most listings are approved within 24 hours.',
        },
        {
          question: 'How much can I earn as a host?',
          answer: 'Earnings vary based on your location, space size, amenities, and pricing. On average, hosts earn between $150-$400 per month. Larger spaces in high-demand areas can earn significantly more. Use our pricing tool to see estimated earnings for your specific space. Remember, Stashly charges a 10% host service fee on each booking. Please remember that potential renters are looking for more convenient and cheaper options than conventional storage providers, so keep that in mind when setting your price, otherwise potential renters may stay with where they are.',
        },
        {
          question: 'When do I get paid?',
          answer: 'Payments are released to hosts 3-5 business days after a booking begins. This gives renters time to confirm the space meets their expectations. Payments are deposited directly to your linked bank account. You can track all payments and earnings in your host dashboard.',
        },
        {
          question: 'What if a renter damages my property?',
          answer: 'Stashly offers a voluntary Host Protection Programme that may provide discretionary assistance in the event of property damage. This programme is not insurance and does not guarantee payment. If damage occurs, document it with photos and contact Stashly support immediately at support@stashly.com.au. We strongly recommend that all hosts obtain their own property and liability insurance before listing a space.',
        },
        {
          question: 'Can I decline a booking request?',
          answer: 'Yes, you have full control over who rents your space. Review each renter\'s profile, ratings, and what they plan to store before accepting. However, frequent declines may affect your listing\'s visibility. We recommend setting clear requirements in your listing to attract suitable renters.',
        },
        {
          question: 'Do I need special insurance as a host?',
          answer: 'Yes. Stashly does not provide insurance to hosts. You are solely responsible for obtaining and maintaining your own insurance, including building insurance, contents insurance, and public liability insurance. Some policies may require notification if you are renting out space. Commercial hosts may need additional business insurance. Stashly\'s Host Protection Programme is a voluntary, discretionary support programme and is not a substitute for proper insurance coverage.',
        },
      ],
    },
    {
      category: 'Safety & Security',
      icon: 'ri-shield-check-line',
      questions: [
        {
          question: 'How does Stashly verify users?',
          answer: 'All users must complete identity verification before listing or booking spaces. Verification is completed via our secure identity verification process using a government-issued ID such as a driver\'s licence or passport. Verified users display a verification badge on their profiles.',
        },
        {
          question: 'What safety measures are in place?',
          answer: 'Stashly implements multiple safety layers: identity verification for all users, secure payment processing, a two-way review system, customer support, and clear community guidelines. We also provide safety tips and best practices for both renters and hosts. Users are encouraged to obtain their own insurance for stored items and listed spaces.',
        },
        {
          question: 'What should I do if something goes wrong?',
          answer: 'For emergencies, contact local authorities first. Then contact Stashly support as soon as possible by emailing support@stashly.com.au or using the contact form on our website. Document any issues with photos and detailed descriptions. Our support team will investigate and help resolve the situation in accordance with our Terms of Service.',
        },
        {
          question: 'How do reviews work?',
          answer: 'After each rental period ends, both renters and hosts can leave reviews. Reviews are public and help build trust in our community. Be honest and constructive in your feedback. Users with consistently poor reviews may have their accounts suspended. You cannot edit reviews once submitted, so review carefully.',
        },
      ],
    },
    {
      category: 'Payments & Pricing',
      icon: 'ri-money-dollar-circle-line',
      questions: [
        {
          question: 'How is pricing determined?',
          answer: 'Hosts set their own prices based on factors like location, space size, amenities, and market demand. Stashly provides pricing recommendations based on similar spaces in your area. Prices are typically quoted per month, but hosts can offer discounts for longer rental periods.',
        },
        {
          question: 'What are the service fees?',
          answer: 'Renters pay a 10% service fee on the booking subtotal, which covers payment processing and customer support. Hosts pay a 10% service fee on their earnings. These fees are clearly displayed before you confirm any transaction. There are no hidden charges.',
        },
        {
          question: 'Are there any additional costs?',
          answer: 'The rental price and service fees are the main costs. However, some hosts may charge additional fees for specific services like 24/7 access, climate control, or security features. These are clearly listed in the space description. Applicable taxes may also apply based on your location.',
        },
        {
          question: 'How do refunds work?',
          answer: 'Refunds are assessed based on the cancellation policy that applied at the time of your booking, and in accordance with the Australian Consumer Law. Refund requests must be submitted in writing to support@stashly.com.au. Stashly will respond within 10 business days. Where a refund is approved, it will be returned to your original payment method.',
        },
      ],
    },
    {
      category: 'Technical Support',
      icon: 'ri-customer-service-2-line',
      questions: [
        {
          question: 'I forgot my password. How do I reset it?',
          answer: 'Click "Log In" then "Forgot Password". Enter your email address and we\'ll send you a password reset link. Click the link in the email and create a new password. If you don\'t receive the email within 10 minutes, check your spam folder or contact support.',
        },
        {
          question: 'How do I update my account information?',
          answer: 'Log in to your account, click on your profile icon, and select "Account Settings". From there you can update your personal information, payment methods, notification preferences, and security settings. Some changes may require identity re-verification for security purposes.',
        },
        {
          question: 'Can I use Stashly on my mobile device?',
          answer: 'Yes! Stashly is fully optimised for mobile browsers. Simply visit stashly.com.au on your smartphone or tablet for the full experience.',
        },
        {
          question: 'How do I contact customer support?',
          answer: 'You can reach our support team through the contact form on our website or by emailing support@stashly.com.au. We typically respond within 2 hours during business hours and within 24 hours on weekends.',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#6366F1] to-[#4F46E5] text-white py-20 pt-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full text-5xl mx-auto mb-6">
            <i className="ri-question-answer-line"></i>
          </div>
          <h1 className="text-5xl font-bold mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-white/90">
            Find answers to common questions about using Stashly
          </p>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {faqCategories.map((cat, index) => (
              <a
                key={index}
                href={`#${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
                className="px-6 py-3 bg-white text-gray-700 font-medium rounded-full hover:bg-[#6366F1] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className={`${cat.icon} mr-2`}></i>
                {cat.category}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Content */}
      <section className="py-20 flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category, catIndex) => (
            <div
              key={catIndex}
              id={category.category.toLowerCase().replace(/\s+/g, '-')}
              className="mb-16"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 flex items-center justify-center bg-[#6366F1] text-white rounded-xl text-2xl">
                  <i className={category.icon}></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{category.category}</h2>
              </div>

              <div className="space-y-4">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = catIndex * 100 + faqIndex;
                  const isOpen = openIndex === globalIndex;

                  return (
                    <div
                      key={faqIndex}
                      className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </span>
                        <i
                          className={`text-2xl text-[#6366F1] transition-transform ${
                            isOpen ? 'ri-subtract-line' : 'ri-add-line'
                          }`}
                        ></i>
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-5 pt-2">
                          <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our support team is here to help you with anything you need
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block px-8 py-4 bg-[#6366F1] text-white font-semibold rounded-lg hover:bg-[#4F46E5] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2"></i>
              Contact Support
            </a>
            <a
              href="/help-center"
              className="inline-block px-8 py-4 bg-white text-gray-900 font-semibold rounded-lg border-2 border-gray-300 hover:border-gray-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line mr-2"></i>
              Visit Help Centre
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
